import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, throwError } from 'rxjs';
import { catchError, concatMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { State } from '../../index';
import { BaseActionTypes } from '../../actions/base.actions';
import { stringifyIfNotNullOrUndefined } from '../../../../helper/helper';
import {
    Contract2DiagnosesicherheitActionTypes,
    DeleteContract2DiagnosesicherheitResponse, GetContract2DiagnosesicherheitenResponse,
    PatchContract2DiagnosesicherheitResponse,
    PostContract2DiagnosesicherheitResponse,
} from '../../actions/mdqx/contract2diagnosesicherheit.actions';
import { HttpService } from '../../../services/http.service';
import { Contract2DiagnosesicherheitEntity } from '../../../entities/mdqx/contract2diagnosesicherheit.entity';
import { DaveActions } from '../../actions/actions';
import { getToken } from '../../selectors/base.selectors';

enum ErrorCodes {
    Get = 'Vertragszuordnungen Abrufen fehlgeschlagen',
    Post = 'Vertragszuordnung Anlegen fehlgeschlagen',
    Patch = 'Vertragszuordnung Bearbeiten fehlgeschlagen',
    Delete = 'Vertragszuordnung Löschen fehlgeschlagen',
}

@Injectable()
export class Contract2DiagnosesicherheitEffects {
    
    get$ = createEffect(() => this.actions$.pipe(
        ofType(Contract2DiagnosesicherheitActionTypes.GetContract2DiagnosesicherheitenRequest),
        map(
            () => `
            {
                contract2Diagnosesicherheit {
                    ${Contract2DiagnosesicherheitEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        switchMap(([query, token]) =>
            this.api.graphQl({ query }, { token }).pipe(
                map(res => res && res.contract2Diagnosesicherheit),
                concatMap(res =>
                    res
                        ? [
                              new GetContract2DiagnosesicherheitenResponse(
                                  res.map(
                                      contract2Diagnosesicherheit =>
                                          new Contract2DiagnosesicherheitEntity(
                                              contract2Diagnosesicherheit,
                                          ),
                                  ),
                              ),
                          ]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Get,
                        Err: err,
                        Caught: caught,
                    }
                }))),
                ),
        ),
    ));

    
    post$ = createEffect(() => this.actions$.pipe(
        ofType(Contract2DiagnosesicherheitActionTypes.PostContract2DiagnosesicherheitRequest),
        map(
            ({ Payload }) => `
            mutation {
                createContract2Diagnosesicherheit(
                    opsCode: ${JSON.stringify(Payload.opsCode)}
                    contractId: ${JSON.stringify(Payload.contractId)}
                    diagnosesicherheitId: ${JSON.stringify(Payload.diagnosesicherheitId)}
                ) {
                    ${Contract2DiagnosesicherheitEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([query, token]) =>
            this.api.graphQl({ query }, { token }).pipe(
                map(res => res && res.createContract2Diagnosesicherheit),
                concatMap(res =>
                    res
                        ? [
                              new PostContract2DiagnosesicherheitResponse(
                                  new Contract2DiagnosesicherheitEntity(res),
                              ),
                          ]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Post,
                        Err: err,
                        Caught: caught,
                    }
                }))),
                ),
        ),
    ));

    
    patch$ = createEffect(() => this.actions$.pipe(
        ofType(Contract2DiagnosesicherheitActionTypes.PatchContract2DiagnosesicherheitRequest),
        map(
            ({ Payload }) => `
            mutation {
                changeContract2Diagnosesicherheit(
                    ${stringifyIfNotNullOrUndefined(Payload, 'contractId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'diagnosesicherheitId')}
                    id: ${JSON.stringify(Payload.id)}
                    ${stringifyIfNotNullOrUndefined(Payload, 'opsCode')}
                ) {
                    ${Contract2DiagnosesicherheitEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([query, token]) =>
            this.api.graphQl({ query }, { token }).pipe(
                map(res => res && res.changeContract2Diagnosesicherheit),
                concatMap(res =>
                    res
                        ? [
                              new PatchContract2DiagnosesicherheitResponse(
                                  new Contract2DiagnosesicherheitEntity(res),
                              ),
                          ]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Patch,
                        Err: err,
                        Caught: caught,
                    }
                }))),
                ),
        ),
    ));

    
    delete$ = createEffect(() => this.actions$.pipe(
        ofType(Contract2DiagnosesicherheitActionTypes.DeleteContract2DiagnosesicherheitRequest),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([{ Payload }, token]) =>
            this.api
                .graphQl(
                    {
                        query: `
                        mutation {
                            deleteContract2Diagnosesicherheit(id: ${JSON.stringify(Payload.id)})
                        }`,
                    },
                    { token },
                )
                .pipe(
                    map(res => res && res.deleteContract2Diagnosesicherheit),
                    concatMap(res =>
                        res
                            ? [new DeleteContract2DiagnosesicherheitResponse(Payload.id)]
                            : throwError('falsy response'),
                    ),
                    catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.Delete,
                            Err: err,
                            Caught: caught,
                        }
                    }))),
                    ),
        ),
    ));

    constructor(
        private actions$: Actions<DaveActions>,
        private api: HttpService,
        private store: Store<State>,
    ) {}
}
